import { AuthGuard } from '@/guards';
import Sidebar from '@/components/Sidebar';
import { PageHeader } from '@/components/common/PageHeader';
import { useLanguage } from '@/contexts/LanguageContext';
import { MagnifyingGlass, ChartBar, Kanban, Code, ChatCenteredText, DotsThreeVertical } from 'phosphor-react';

const MOCK_SKILLS = [
  { id: '1', name: 'Analista de Dados', description: 'Agente especializado em processar grandes volumes de dados e gerar insights.', icon: <ChartBar className="text-blue-500" /> },
  { id: '2', name: 'Gestor de Projetos', description: 'Agente focado em organizar tarefas, prazos e comunicação de equipe.', icon: <Kanban className="text-zinc-900 dark:text-white" /> },
  { id: '3', name: 'Revisor de Código', description: 'Agente que analisa Pull Requests e sugere melhorias seguindo padrões de Clean Code.', icon: <Code className="text-purple-500" /> },
  { id: '4', name: 'Especialista em Suporte', description: 'Agente treinado na sua base de conhecimento para suporte técnico imediato.', icon: <ChatCenteredText className="text-green-500" /> },
];

export default function SkillsPage() {
  const { t } = useLanguage();

  return (
    <AuthGuard>
      <div className="flex h-screen theme-bg overflow-hidden">
        <div className="flex-shrink-0">
          <Sidebar />
        </div>

        <main className="flex-1 min-w-0 overflow-y-auto flex flex-col">
          <PageHeader
            title={t('sidebar.skills')}
            subtitle="Crie e gerencie agentes customizados com habilidades específicas para o seu negócio."
            action={
              <button
                type="button"
                className="inline-flex items-center justify-center px-4 h-8 rounded-lg text-xs font-medium transition-all cursor-pointer disabled:pointer-events-none bg-[color:var(--nt-primary)] text-white fill-white shadow-[inset_0_-0.125rem_0_0_var(--nt-primary-inset-bottom),inset_0_0.125rem_0_0_var(--nt-primary-inset-top)] hover:brightness-95 hover:shadow-[inset_0_-0.125rem_0_0_var(--nt-primary-inset-bottom-hover),inset_0_0.125rem_0_0_var(--nt-primary-inset-top-hover)]"
              >
                <svg className="inline-flex size-4 fill-inherit" width="16" height="16" viewBox="0 0 16 16">
                  <path d="M8.663 3.33l-.001 4h4.001v1.333H8.662l.001 4H7.33v-4h-4V7.33h4v-4z"></path>
                </svg>
                <span className="ml-2">Criar Agente</span>
              </button>
            }
          />

          <div className="flex-1 p-8">
            <div className="max-w-6xl mx-auto">
              {/* Search */}
              <div className="flex items-center gap-4 mb-8">
                <div className="relative flex-1 max-w-md">
                  <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                  <input
                    type="text"
                    placeholder="Buscar agentes ou habilidades..."
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border theme-border bg-white dark:bg-zinc-900/50 outline-none focus:ring-2 focus:ring-[color:var(--nt-primary)]/20 transition-all font-medium text-sm"
                  />
                </div>
              </div>

              {/* Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {MOCK_SKILLS.map((skill) => (
                  <div key={skill.id} className="group relative flex flex-col p-6 rounded-2xl border theme-border bg-white dark:bg-zinc-900/50 hover:shadow-xl hover:shadow-black/5 dark:hover:shadow-none transition-all duration-300">
                    <div className="flex items-start justify-between mb-4">
                      <div className="size-12 rounded-xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-2xl">
                        {skill.icon}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="px-2 py-1 rounded-md bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-[10px] font-bold uppercase tracking-wider">
                          Ativo
                        </div>
                        <button className="p-1 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 theme-text-muted transition-colors">
                          <DotsThreeVertical size={20} weight="bold" />
                        </button>
                      </div>
                    </div>
                    <h3 className="text-[15px] font-semibold theme-text mb-1.5 group-hover:text-[color:var(--nt-primary)] transition-colors">{skill.name}</h3>
                    <p className="text-xs theme-text-muted leading-relaxed line-clamp-2">{skill.description}</p>
                    
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    </AuthGuard>
  );
}
